import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import type { FC } from 'react';

const Navbar: FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="section-container flex items-center justify-between h-16 md:h-[72px]">
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-grid-line text-lg"></i>
          </div>
          <div>
            <span className="font-heading text-lg md:text-xl font-semibold text-foreground-900 leading-none">
              Kakobuy
            </span>
            <span className="block text-xs text-foreground-500 leading-none mt-0.5">Polska Forum</span>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <Link to="/" className="px-3 py-2 text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors whitespace-nowrap rounded-md hover:bg-primary-50">
            <i className="ri-home-4-line mr-1.5 text-sm"></i>
            Strona Główna
          </Link>
          <a href="#categories" className="px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap rounded-md hover:bg-background-100">Kategorie</a>
          <a href="#stats" className="px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap rounded-md hover:bg-background-100">Statystyki</a>
          <a href="#blog" className="px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap rounded-md hover:bg-background-100">Artykuły</a>
          <a href="#safety" className="px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap rounded-md hover:bg-background-100">Bezpieczeństwo</a>
          <a href="#faq" className="px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap rounded-md hover:bg-background-100">FAQ</a>
          <Link to="/artykul/kakobuy-spreadsheet-polska-kompletny-przewodnik" className="px-3 py-2 text-sm font-medium text-accent-600 hover:text-accent-700 transition-colors whitespace-nowrap rounded-md hover:bg-accent-50">
            <i className="ri-book-open-line mr-1.5 text-sm"></i>
            Poradnik Zakupów
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="btn-primary px-4 py-2 text-sm bg-primary-500 text-background-50 hover:bg-primary-600 rounded-md"
          >
            Spreadsheet
          </a>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
          aria-label="Menu"
        >
          <i className={`text-xl text-foreground-700 ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 max-h-[80vh] overflow-y-auto">
          <div className="section-container py-4 flex flex-col gap-1">
            <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-primary-600 rounded-md hover:bg-primary-50 transition-colors">
              <i className="ri-home-4-line text-base"></i>
              Strona Główna
            </Link>

            <Link to="/artykul/kakobuy-spreadsheet-polska-kompletny-przewodnik" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-accent-600 rounded-md hover:bg-accent-50 transition-colors">
              <i className="ri-book-open-line text-base"></i>
              Poradnik Zakupów
            </Link>

            <div className="border-t border-background-200 my-2"></div>

            <div>
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm text-foreground-700 font-semibold rounded-md hover:bg-background-100 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-grid-line text-base text-secondary-600"></i>
                  Kategorie Produktów
                </span>
                <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileCatOpen && (
                <div className="ml-4 mt-1 space-y-0.5 animate-scale-in">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => { setMobileOpen(false); setMobileCatOpen(false); }}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-600 hover:text-foreground-900 rounded-md hover:bg-background-100 transition-colors"
                    >
                      <div className="w-6 h-6 flex items-center justify-center rounded bg-secondary-100 text-secondary-600 flex-shrink-0">
                        <i className={`${cat.icon} text-[10px]`}></i>
                      </div>
                      <span>{cat.namePl}</span>
                      <span className="ml-auto text-[10px] text-foreground-400">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-background-200 my-2"></div>

            <a href="#blog" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-foreground-600 hover:text-foreground-900 rounded-md hover:bg-background-100 transition-colors">
              <i className="ri-article-line text-base"></i>
              Artykuły SEO
            </a>
            <a href="#stats" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-foreground-600 hover:text-foreground-900 rounded-md hover:bg-background-100 transition-colors">
              <i className="ri-bar-chart-line text-base"></i>
              Statystyki
            </a>
            <a href="#safety" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-foreground-600 hover:text-foreground-900 rounded-md hover:bg-background-100 transition-colors">
              <i className="ri-shield-check-line text-base"></i>
              Bezpieczeństwo
            </a>
            <a href="#faq" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-foreground-600 hover:text-foreground-900 rounded-md hover:bg-background-100 transition-colors">
              <i className="ri-question-answer-line text-base"></i>
              FAQ
            </a>

            <div className="border-t border-background-200 my-2"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 rounded-md transition-colors"
            >
              Kakobuy Spreadsheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;